import { NotificationType } from "../../types/Notifications/NotificationBase";
import { DoubleShift } from "../../types/Shift";
import { LinkTarget } from "./linkTarget";
import { STOPPAGES_AREA } from "./dashboardLinks";
import { stoppagesLink } from "./stoppagesDeepLink";

const SHIFT_BY_CODE: Record<string, DoubleShift> = { D: "DAY", N: "NIGHT" };

type StoppageNotification = Extract<
  NotificationType,
  { type: "StoppageSetAsAvailableNotification" | "StoppageSetAsUnavailableNotification" }
>;

/**
 * A stoppage notification can arrive while a past shift is on screen, so the link carries the shift
 * the stoppage belongs to. Older notifications carry neither that nor the stoppage id, and still
 * get as far as the machine.
 */
const stoppageTarget = (notification: StoppageNotification): LinkTarget | null => {
  if (!notification.stoppageId && !notification.plantRef) return null;

  const shift = notification.dayOrNight ? SHIFT_BY_CODE[notification.dayOrNight] : null;
  const carriesShift = !!notification.shiftDate && !!shift;

  return {
    to: stoppagesLink({
      stoppageId: notification.stoppageId ?? null,
      machine: notification.plantRef || null,
      date: carriesShift ? notification.shiftDate : null,
      shift: carriesShift ? shift : null,
    }),
    area: STOPPAGES_AREA,
  };
};

/**
 * The page a notification is about, for the notification types that record their subject on one.
 * Switching on the type rather than on the fields present keeps a future notification that happens
 * to carry a plantRef from being offered as a stoppage.
 */
export const notificationTarget = (notification: NotificationType): LinkTarget | null => {
  switch (notification.type) {
    case "StoppageSetAsAvailableNotification":
    case "StoppageSetAsUnavailableNotification":
      return stoppageTarget(notification);

    default:
      return null;
  }
};
